import React from 'react';
import { ReactComponent as LoopIcon } from '../assets/icons/loop.svg';

function formatTime(ms) {
    if (ms == null || isNaN(ms)) return '0:00';
    const totalSec = ms / 1000;
    const m = Math.floor(totalSec / 60);
    const s = Math.floor(totalSec % 60);
    const tenths = Math.floor((totalSec * 10) % 10);
    return `${m}:${s.toString().padStart(2, '0')}.${tenths}`;
}

// Short m:ss for loop ranges (no tenths — keeps the pad compact).
function shortTime(ms) {
    if (ms == null || isNaN(ms)) return '0:00';
    const total = Math.floor(ms / 1000);
    return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`;
}

const LOOP_SLOT_LETTERS = 'ABCDEFGH';
const LOOP_KIND_LABEL = {
    intro: 'Intro', outro: 'Outro', drop: 'Drop', break: 'Break',
    beat: 'Groove', section: 'Section', cue: 'Loop',
};

/**
 * Compact rekordbox-style cue + loop pads — cues and loops share ONE horizontal
 * row (labelled inline) so both are visible at a glance.
 * Props:
 *   cues, loops           : CuePoint[]
 *   onRemove(cueId)        : remove a pad
 *   onSeek(positionMs)     : jump the playhead
 *   onScaleLoop(id,factor) : ×2 / ÷2 a loop's length
 *   activeLoopId           : id of the loop currently looping in the player
 *   onToggleLoopActive(id) : toggle player looping for a loop
 */
const CueList = ({ cues = [], loops = [], loading = false, onRemove, onSeek, onScaleLoop, activeLoopId, onToggleLoopActive, hoveredId, onHoverItem, rbActiveLoopId, onToggleRbActive, onLoopContextMenu }) => {
    const hoverProps = (id) => ({
        onMouseEnter: () => onHoverItem && onHoverItem(id),
        onMouseLeave: () => onHoverItem && onHoverItem(null),
    });
    if (!cues.length && !loops.length) {
        // Cues are read from disk per track (a Python subprocess). Until that
        // resolves we can't tell "none" from "not read yet" — show a skeleton
        // so the track never flashes the definitive empty message first.
        if (loading) {
            return (
                <div className="cue-pads cue-pads-loading" aria-busy="true">
                    <span className="cue-loading-pad" />
                    <span className="cue-loading-pad" />
                    <span className="cue-loading-pad" />
                    <span className="cue-loading-label">Reading cues &amp; loops…</span>
                </div>
            );
        }
        return (
            <div className="cue-pads cue-pads-empty">
                <span className="cue-empty-title">No cues or loops yet</span>
                <span className="cue-empty-hint">Hit <strong>Generate</strong> to auto-detect them — or on the waveform: double-click for a cue, shift-drag for a loop, or right-click to choose.</span>
            </div>
        );
    }

    const orderedCues = cues.slice().sort((a, b) => {
        const ah = a.hotcueIndex >= 0;
        const bh = b.hotcueIndex >= 0;
        if (ah && bh) return a.hotcueIndex - b.hotcueIndex;
        if (ah) return -1;
        if (bh) return 1;
        return a.positionMs - b.positionMs;
    });
    const orderedLoops = loops.slice().sort((a, b) => a.positionMs - b.positionMs);

    const sourceDot = (src) => <span className={`cue-pad-source ${src || 'auto'}`} title={src || 'auto'} />;

    return (
        <div className="cue-pads-row">
            {orderedCues.length > 0 && (
                <span className="cue-pad-group-label" title="Press 1–8 on your keyboard to jump to a cue">Cues</span>
            )}
            {orderedCues.map((cue) => {
                const isHot = cue.hotcueIndex != null && cue.hotcueIndex >= 0;
                const color = isHot ? (cue.color || '#00bcd4') : '#9aa0a6';
                const label = isHot ? cue.hotcueIndex + 1 : 'M';
                return (
                    <button
                        key={cue.id}
                        className={`cue-pad ${isHot ? 'hot' : 'memory'} ${hoveredId === cue.id ? 'hover-sync' : ''}`}
                        style={{ '--pad-color': color }}
                        onClick={() => onSeek && onSeek(cue.positionMs)}
                        title={isHot ? `Hot Cue ${label} — click to jump` : 'Memory cue — click to jump'}
                        {...hoverProps(cue.id)}
                    >
                        {sourceDot(cue.source)}
                        <span className="cue-pad-num">{label}</span>
                        <span className="cue-pad-time">{formatTime(cue.positionMs)}</span>
                        <span
                            className="cue-pad-remove"
                            onClick={(e) => { e.stopPropagation(); onRemove && onRemove(cue.id); }}
                            title="Remove cue"
                        >
                            ×
                        </span>
                    </button>
                );
            })}

            {orderedLoops.length > 0 && (
                <span className="cue-pad-group-label loops" title="Press A–H on your keyboard to toggle a loop">Loops</span>
            )}
            {orderedLoops.map((loop) => {
                const color = loop.color || '#00bcd4';
                const letter = loop.hotcueIndex >= 0 ? LOOP_SLOT_LETTERS[loop.hotcueIndex] || '•' : '•';
                const range = `${shortTime(loop.positionMs)}–${shortTime(loop.endPositionMs)}`;
                // Detected loops carry a kind; loops read back from a file/app only
                // carry their saved name ("Groove 8 bars") — show that, not "Loop".
                const kindLabel = LOOP_KIND_LABEL[loop.kind] || (loop.name && loop.name.trim() ? loop.name.split(' · ')[0] : 'Loop');
                const isActive = loop.id === activeLoopId;
                return (
                    <div
                        key={loop.id}
                        className={`cue-pad loop-pad ${isActive ? 'active' : ''} ${hoveredId === loop.id ? 'hover-sync' : ''}`}
                        style={{ '--pad-color': color }}
                        onClick={() => onSeek && onSeek(loop.positionMs)}
                        onContextMenu={onLoopContextMenu ? (e) => { e.preventDefault(); e.stopPropagation(); onLoopContextMenu(e, loop); } : undefined}
                        title={`${kindLabel} · ${loop.bars ? loop.bars + ' bars · ' : ''}${range} — click to jump to start${onLoopContextMenu ? ' · right-click for Active Loop' : ''}`}
                        role="button"
                        {...hoverProps(loop.id)}
                    >
                        <span className="cue-pad-num">{letter}</span>
                        <span className="loop-pad-info">
                            <span className="loop-pad-kind">{kindLabel}</span>
                            <span className="loop-pad-sub">{loop.bars ? `${loop.bars} bars` : 'loop'}</span>
                        </span>
                        <span
                            className={`loop-active-toggle ${isActive ? 'on' : ''}`}
                            onClick={(e) => { e.stopPropagation(); onToggleLoopActive && onToggleLoopActive(loop.id); }}
                            title={isActive ? 'Looping — click to stop' : 'Loop this section in the player'}
                        >
                            <LoopIcon className="loop-active-icon" />
                        </span>
                        <span className="loop-pad-scale">
                            <span
                                className="loop-mini"
                                onClick={(e) => { e.stopPropagation(); onScaleLoop && onScaleLoop(loop.id, 2); }}
                                title="Double the loop length"
                            >×2</span>
                            <span
                                className="loop-mini"
                                onClick={(e) => { e.stopPropagation(); onScaleLoop && onScaleLoop(loop.id, 0.5); }}
                                title="Halve the loop length"
                            >÷2</span>
                        </span>
                        {onToggleRbActive && (
                            <span
                                className={`loop-rb-active ${loop.id === rbActiveLoopId ? 'on' : ''}`}
                                onClick={(e) => { e.stopPropagation(); onToggleRbActive(loop); }}
                                title={loop.id === rbActiveLoopId
                                    ? 'Active Loop (Rekordbox) — auto-engages when playback reaches it. Click to turn off.'
                                    : 'Set as Active Loop (Rekordbox) — auto-engages when playback reaches it. One per track.'}
                            >
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                                </svg>
                            </span>
                        )}
                        <span
                            className="cue-pad-remove"
                            onClick={(e) => { e.stopPropagation(); onRemove && onRemove(loop.id); }}
                            title="Remove loop"
                        >
                            ×
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export default CueList;
